// Reconcile (waste count) editability rules.
//
// These mirror the backend validation for `PUT /v1/reconciles/{uuid}/details`,
// which returns `409 Conflict` when editing is not allowed. Keeping the rules in
// one place avoids hardcoded status strings scattered across the JSX and keeps
// the UI in sync with the API contract.

// Reconcile statuses (see also Dashboard/RouteTable). Reconcile details can only
// be edited while the reconcile is still "submitted" (รอการเงินตรวจสอบ).
export const RECONCILE_STATUS = {
  PENDING: "pending",
  IN_PROGRESS: "in progress",
  SUBMITTED: "submitted",
  FINANCE_APPROVED: "finance approved",
  ACCOUNT_APPROVED: "account approved",
  APPROVED: "approved",
} as const;

export type ReconcileStatus =
  (typeof RECONCILE_STATUS)[keyof typeof RECONCILE_STATUS];

// Linked Sale Daily Report statuses (see Report/ReportList/data).
export const SALE_REPORT_STATUS = {
  DRAFT: "draft",
  BRANCH_FINANCE_SUBMITTED: "branch finance submitted",
  BRANCH_ACCOUNT_APPROVED: "branch account approved",
  BRANCH_ACCOUNT_REQUEST_CHANGE: "branch account request change",
  HO_REQUEST_CHANGE: "ho request change",
  HO_APPROVED: "ho approved",
  REJECTED: "rejected",
} as const;

export type SaleReportStatus =
  (typeof SALE_REPORT_STATUS)[keyof typeof SALE_REPORT_STATUS];

// Sale report statuses that still allow the linked reconcile details to be edited.
export const EDITABLE_SALE_REPORT_STATUSES: string[] = [
  SALE_REPORT_STATUS.DRAFT,
  // Branch accounting sent the report back to branch finance. Editable by
  // BOTH desks: finance fixes and re-submits, or accounting corrects it
  // themselves and approves.
  SALE_REPORT_STATUS.BRANCH_ACCOUNT_REQUEST_CHANGE,
  SALE_REPORT_STATUS.HO_REQUEST_CHANGE,
  SALE_REPORT_STATUS.REJECTED,
];

// Sale report statuses where the report has entered the finance approval process
// and the reconcile details can no longer be edited.
export const FINANCE_APPROVAL_SALE_REPORT_STATUSES: string[] = [
  SALE_REPORT_STATUS.BRANCH_FINANCE_SUBMITTED,
  SALE_REPORT_STATUS.BRANCH_ACCOUNT_APPROVED,
  SALE_REPORT_STATUS.HO_APPROVED,
];

// Tooltip / toast messages shown when editing is disabled.
export const RECONCILE_NOT_SUBMITTED_MESSAGE =
  "แก้ไขได้เฉพาะรายการที่ส่งตรวจแล้วเท่านั้น";
export const SALE_REPORT_IN_FINANCE_MESSAGE =
  "ไม่สามารถแก้ไขได้ เนื่องจากรายงานการขายเข้าสู่ขั้นตอนการเงินแล้ว";
// Generic fallback for an unexpected 409 message.
export const RECONCILE_EDIT_BLOCKED_MESSAGE =
  "ไม่สามารถแก้ไขรายการของเสียได้ในขณะนี้";

export interface ReconcileEditability {
  // Reconcile status, when the API exposes it. `undefined`/`null` means the
  // status is unknown — in that case the reconcile-status rule is not enforced
  // on the client and the backend 409 remains the source of truth.
  reconcileStatus?: string | null;
  // Linked Sale Daily Report status. `undefined`/`null` means no report exists
  // yet, which is still editable.
  saleReportStatus?: string | null;
}

/**
 * Returns the reason editing is blocked, or `null` when editing is allowed.
 *
 * Editing is allowed only when BOTH hold:
 *  1. The reconcile status is `submitted` (when known).
 *  2. The linked sale report does not exist yet, or its status is still
 *     editable (`draft`, `branch account request change`,
 *     `ho request change`, `rejected`).
 */
export function getReconcileEditDisabledReason({
  reconcileStatus,
  saleReportStatus,
}: ReconcileEditability): string | null {
  // A report that has entered finance approval is locked regardless of the
  // reconcile status — check it first so the more specific message wins.
  if (
    saleReportStatus &&
    FINANCE_APPROVAL_SALE_REPORT_STATUSES.includes(saleReportStatus)
  ) {
    return SALE_REPORT_IN_FINANCE_MESSAGE;
  }

  // The reconcile itself must be submitted. Only enforced when the status is
  // known; otherwise defer to the backend (409).
  if (
    reconcileStatus != null &&
    reconcileStatus !== RECONCILE_STATUS.SUBMITTED
  ) {
    return RECONCILE_NOT_SUBMITTED_MESSAGE;
  }

  return null;
}

export function canEditReconcileDetails(input: ReconcileEditability): boolean {
  return getReconcileEditDisabledReason(input) === null;
}

/**
 * Maps a backend `409 Conflict` message to a user-friendly Thai message that
 * matches the disabled-button tooltips.
 */
export function mapReconcileConflictMessage(backendMessage?: string): string {
  const message = backendMessage?.toLowerCase() ?? "";

  if (message.includes("finance approval process")) {
    return SALE_REPORT_IN_FINANCE_MESSAGE;
  }
  if (message.includes("reconcile status is submitted")) {
    return RECONCILE_NOT_SUBMITTED_MESSAGE;
  }
  return RECONCILE_EDIT_BLOCKED_MESSAGE;
}
